// MIDAS V3 — New Chat dialog (Phase 4)
//
// Centered modal that opens from the rail's "+ New chat" button (both
// expanded and collapsed variants). Lets the user start either:
//   • Platform-mode chat (managers / admins only — gated on `user.role`)
//   • Machine-context chat (filtered by user scope)
//
// Source: ToDo/MidasChat/Claude_Design_files/machineid/
//         design_handoff_midas_v3/chat-window.jsx — `NewChatDialog` (lines 361-499)
//
// Data sourcing (NOT the handoff's `MACHINES` fixture):
//   • Machines come from `customerMachinery.machines` (same source as v2's
//     MidasFleetChipsV2). Worker scoping is enforced by the backend — the
//     slice already only contains machines the current user is entitled to
//     see. This mirrors v2 exactly.
//   • Run-state / status badges REMOVED per owner ruling 2026-08-06. The old
//     pill+pip were derived from `activeServiceRequestCount` (SR-count, not
//     real telemetry) and posed as machine run-state. The owner does not want
//     that signal, nor any live/not-live badge, until a full-telemetry
//     customer pilot defines what's actually relevant. Rows now show name /
//     type / SN / location + the doc-readiness cluster only. (The SR→status
//     mapping helper ./v3-utils/machineStatus.ts was deleted as dead code.)
//
// Dispatch pattern (mirrors v2's PlatformModeCardV2 + MidasSidebarV2):
//   Platform pick → startNewChat() + setActiveThreadId(null) + setMachineContext(null)
//   Machine pick  → startNewChat() + setActiveThreadId(null) + setNewThreadPending()
//                   + setMachineContextDirect({ machineId, model, ... })
//                   + (debounced) chatBotApi.setMachineContext(machineId)
//
// No `kind` field added to startNewChat — v2 already differentiates Platform
// vs Machine via the subsequent setMachineContext dispatch. Mirroring that
// exact pattern keeps chatBotSlice.ts untouched and avoids any v2 regression
// surface. (See spec §5 — backwards-compatible alternative.)
//
// Permission gating:
//   isManager = user?.role !== Roles.WORKER (same as the rest of the app —
//   see customer/operations/**/canManage and customer/useCustomerRoutes.tsx).
//   Workers do not see the Platform card. Backend already rejects worker
//   platform-mode requests (SEC-REQ-1, verified in v2).
//
// Keyboard:
//   • Esc → close
//   • ↑ / ↓ → move focus among rows (Platform option + visible machine rows)
//   • Enter → activate focused row
//   • Tab / Shift+Tab → cycle within the dialog (manual focus trap)
//
// No MUI, no @emotion, no `sx`, no `dt` tokens. The modal is a plain <div>
// portal'd to document.body via ReactDOM.createPortal so it always renders
// above the rest of the app regardless of stacking context.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";

import {
  useAppDispatch,
  useAppSelector,
} from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { Roles } from "../../../../account/models/AccountModels";
import {
  setMachineContext,
  setMachineContextDirect,
  setNewThreadPending,
  startNewChat,
} from "../../../../shared/chatBot/chatBotSlice";
import chatBotApi from "../../../../shared/chatBot/ChatBotApi";
import { getMachines } from "../../../machinery/customerMachinerySlice";
import { Machine } from "../../../machinery/models/models";
import { hasDocs } from "../../../machinery/utils/midasReadiness";
import { setActiveThreadId } from "../../midasPageSlice";
import { resolveV3PaletteClass } from "../../midasV3Palette";
// DR-5: readiness cluster (doc chip + OEM star + (i) popover) rendered left of
// the RUNNING chip on machine rows that have MIDAS docs.
import NewChatReadinessCluster from "./NewChatReadinessCluster";

// ============================================================
// Props
// ============================================================

interface NewChatDialogV3Props {
  open: boolean;
  onClose: () => void;
  /** Element to restore focus to when the dialog closes. */
  triggerRef?: React.RefObject<HTMLElement>;
}

// ============================================================
// Flatten the grouped machines map (same shape v2 uses).
// ============================================================

const flattenMachines = (
  machinesMap: Record<string, Machine[] | undefined> | null | undefined
): Machine[] => {
  if (!machinesMap || typeof machinesMap !== "object") return [];
  const flat: Machine[] = [];
  Object.values(machinesMap).forEach((g) => {
    if (Array.isArray(g)) flat.push(...(g as Machine[]));
  });
  return flat;
};

// Build the location line per Round 5 spec (matches MidasFleetChipsV2).
const formatLocation = (m: Machine): string => {
  const primary = m.locationName?.trim() || "";
  const secondary = m.parentLocationName?.trim() || "";
  if (primary && secondary) return `${primary} · ${secondary}`;
  if (primary) return primary;
  if (secondary) return secondary;
  return "—";
};

// ============================================================
// NewChatDialogV3
// ============================================================

const NewChatDialogV3: React.FC<NewChatDialogV3Props> = ({
  open,
  onClose,
  triggerRef,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { translate } = useTranslation("chatBot");
  const t = (k: string, f: string) =>
    translate(k as any, { defaultValue: f }) as string;

  // Manager gating — `user?.role !== Roles.WORKER` mirrors the pattern used
  // across customer/operations/** and Header.tsx. Admin/Manager see Platform;
  // Worker doesn't. Viewer never reaches /midas-v3 (ViewerRouteGuard).
  // MidasPilot is excluded EXPLICITLY (2026-08-19, TAJMAC-PILOT review F5): the
  // deny-list shape silently promoted the new role to manager, offering an external
  // pilot the FLEET-WIDE "Platform" chat mode. Its tenant holds one machine, so the
  // mode is pointless there and the framing is wrong.
  const user = useAppSelector((s) => s.account.user);
  const isManager =
    !!user?.role && user.role !== Roles.WORKER && user.role !== Roles.MIDAS_PILOT;

  const machinesMap = useAppSelector((s) => s.customerMachinery.machines);
  const machineryLoading = useAppSelector(
    (s) => s.customerMachinery.loading
  );

  const allMachines = useMemo(
    () => flattenMachines(machinesMap),
    [machinesMap]
  );

  // Lazy-fetch machines if the slice is empty on open. Mirrors v2.
  useEffect(() => {
    if (open && allMachines.length === 0 && !machineryLoading) {
      dispatch(getMachines({}));
    }
  }, [open, allMachines.length, machineryLoading, dispatch]);

  // ── local UI state ─────────────────────────────────────────
  const [query, setQuery] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(0);

  // Reset state every time the dialog opens.
  useEffect(() => {
    if (open) {
      setQuery("");
      setFocusedIndex(0);
    }
  }, [open]);

  // ── filtered list ─────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allMachines;
    return allMachines.filter((m) => {
      const model = (m.model || "").toLowerCase();
      const sn = (m.serialNumber || "").toLowerCase();
      const brand = (
        m.customBrandName ||
        m.manufacturer?.name ||
        ""
      ).toLowerCase();
      const typeName = (m.type?.name || "").toLowerCase();
      const loc = (
        (m.locationName || "") +
        " " +
        (m.parentLocationName || "")
      ).toLowerCase();
      return (
        model.includes(q) ||
        sn.includes(q) ||
        brand.includes(q) ||
        typeName.includes(q) ||
        loc.includes(q)
      );
    });
  }, [allMachines, query]);

  // ── DR-5: split into "has MIDAS docs" and de-emphasized "no docs" groups ──
  // Grouping key is `hasDocs` (NOT live/telemetry — a RUNNING machine with no
  // docs still drops to the bottom group; spec §6 rule 3). Machines without a
  // catalogModel have no readiness → no docs → bottom group. The util is the
  // single source of truth for the derivation.
  const withDocs = useMemo(
    () => filtered.filter((m) => hasDocs(m.catalogModel?.readiness)),
    [filtered]
  );
  const noDocs = useMemo(
    () => filtered.filter((m) => !hasDocs(m.catalogModel?.readiness)),
    [filtered]
  );
  // Visual + keyboard-nav order: docs group first, then the de-emphasized
  // group. `ordered` is the authoritative index space for ↑/↓ + Enter so the
  // cursor walks the two groups seamlessly.
  const ordered = useMemo(() => [...withDocs, ...noDocs], [withDocs, noDocs]);

  // ── keyboard nav model ─────────────────────────────────────
  // Index 0 = Platform card (when visible). Machines start at 1 (or 0 if no
  // Platform card). Capped to visible range.
  const platformOffset = isManager ? 1 : 0;
  const totalRows = platformOffset + ordered.length;

  // Clamp focusedIndex when list size changes.
  useEffect(() => {
    if (focusedIndex >= totalRows) {
      setFocusedIndex(Math.max(0, totalRows - 1));
    }
  }, [totalRows, focusedIndex]);

  // ── refs ───────────────────────────────────────────────────
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const platformBtnRef = useRef<HTMLButtonElement | null>(null);
  const machineBtnRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Cache the trigger element on open so we can restore focus on close.
  const cachedTriggerRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (open) {
      cachedTriggerRef.current =
        triggerRef?.current ||
        (document.activeElement as HTMLElement | null) ||
        null;
      // Move focus into the dialog: search input is the most useful entry.
      // Defer to next tick so the portal node exists.
      const id = window.setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => window.clearTimeout(id);
    }
    // On close, restore focus to the trigger.
    const el = cachedTriggerRef.current;
    if (el && typeof el.focus === "function") {
      el.focus();
    }
    cachedTriggerRef.current = null;
    return undefined;
  }, [open, triggerRef]);

  // ── dispatch helpers ─────────────────────────────────────
  const pickPlatform = useCallback(() => {
    dispatch(startNewChat());
    dispatch(setActiveThreadId(null));
    // Tell the bot (best-effort) to drop machine mode AND clear local context.
    dispatch(setMachineContext(null));
    onClose();
    navigate("/midas");
  }, [dispatch, navigate, onClose]);

  // Debounced bot-side setMachineContext. Mirrors v2's MidasSidebarV2 logic
  // so the bot exits/enters machine mode without spamming the API.
  const contextTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (contextTimerRef.current) {
        clearTimeout(contextTimerRef.current);
      }
    },
    []
  );

  const pickMachine = useCallback(
    (machine: Machine) => {
      dispatch(startNewChat());
      dispatch(setActiveThreadId(null));
      dispatch(setNewThreadPending());
      dispatch(
        setMachineContextDirect({
          machineId: machine.id,
          model: machine.model,
          serialNumber: machine.serialNumber,
          internalNumber: machine.internalNumber,
          system: machine.system,
          manufacturerName:
            machine.customBrandName || machine.manufacturer?.name || "",
          typeName: machine.type?.name,
          axisConfigName: machine.axisConfiguration?.name,
          controlSystemBrandName: machine.controlSystemBrand?.name,
          imageContent: machine.image?.content,
          description: machine.description,
          productionYear: machine.productionYear,
        })
      );
      if (contextTimerRef.current) {
        clearTimeout(contextTimerRef.current);
      }
      contextTimerRef.current = setTimeout(() => {
        chatBotApi.setMachineContext(machine.id).catch(() => {
          /* Best-effort — bot may be offline */
        });
      }, 300);
      onClose();
      navigate("/midas");
    },
    [dispatch, navigate, onClose]
  );

  // Activate whatever the keyboard cursor is sitting on.
  const activateFocused = useCallback(() => {
    if (totalRows === 0) return;
    if (isManager && focusedIndex === 0) {
      pickPlatform();
      return;
    }
    const m = ordered[focusedIndex - platformOffset];
    if (m) pickMachine(m);
  }, [
    totalRows,
    isManager,
    focusedIndex,
    ordered,
    platformOffset,
    pickPlatform,
    pickMachine,
  ]);

  // ── global key handler ─────────────────────────────────────
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setFocusedIndex((i) => Math.min(totalRows - 1, i + 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setFocusedIndex((i) => Math.max(0, i - 1));
        return;
      }
      if (e.key === "Enter") {
        // Only intercept Enter when the focus is in the search input — the
        // visual cursor is what should activate. If focus is on a real
        // button, default Enter behaviour (click that button) still works.
        if (document.activeElement === searchInputRef.current) {
          e.preventDefault();
          activateFocused();
        }
        return;
      }
      // Manual focus trap on Tab / Shift+Tab — cycle within the dialog.
      if (e.key === "Tab") {
        const root = dialogRef.current;
        if (!root) return;
        const focusables = root.querySelectorAll<HTMLElement>(
          'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const enabled = Array.from(focusables).filter(
          (el) => !el.hasAttribute("disabled") && el.tabIndex !== -1
        );
        if (enabled.length === 0) return;
        const first = enabled[0];
        const last = enabled[enabled.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, totalRows, onClose, activateFocused]);

  // ── i18n labels ─────────────────────────────────────────
  const labelTitle = t("midas.v3.newChatDialog.title", "New chat");
  const labelEyebrow = t(
    "midas.v3.newChatDialog.eyebrow",
    "Choose context"
  );
  const labelCloseAria = t("midas.v3.newChatDialog.closeAria", "Close dialog");
  const labelPlatformTitle = t(
    "midas.v3.newChatDialog.platform.title",
    "PLATFORM · MACHINE ID"
  );
  const labelPlatformDesc = t(
    "midas.v3.newChatDialog.platform.description",
    "Chat about the platform itself — add machines, manage users, ask about reports across the fleet."
  );
  const labelPlatformBadge = t(
    "midas.v3.newChatDialog.platform.badge",
    "MANAGERS"
  );
  const labelDivider = t(
    "midas.v3.newChatDialog.divider",
    "Or pick a machine"
  );
  const labelSearchPlaceholder = t(
    "midas.v3.newChatDialog.search.placeholder",
    "Search by name, S/N, or location…"
  );
  const labelFooterNavigate = t(
    "midas.v3.newChatDialog.footer.navigate",
    "↑↓ navigate"
  );
  const labelFooterSelect = t(
    "midas.v3.newChatDialog.footer.select",
    "↵ select"
  );
  const labelFooterClose = t(
    "midas.v3.newChatDialog.footer.close",
    "esc close"
  );
  const labelManagerAccess = t(
    "midas.v3.newChatDialog.footer.managerAccess",
    "Manager access"
  );
  const labelOperatorAccess = t(
    "midas.v3.newChatDialog.footer.operatorAccess",
    "Operator access"
  );
  const labelEmptyState = t(
    "midas.v3.newChatDialog.emptyState",
    "No machines match your search."
  );
  const labelNoMachines = t(
    "midas.v3.newChatDialog.noMachines",
    "No machines yet."
  );
  // DR-5: de-emphasized "no MIDAS docs" group + per-row live-chat note.
  const labelNoDocsGroup = t(
    "midas.v3.newChatDialog.readiness.noDocsGroup",
    "NO MIDAS DOCS YET"
  );
  const labelLiveChatOnly = t(
    "midas.v3.newChatDialog.readiness.liveChatOnly",
    "live chat only"
  );

  // ── DR-5: single row renderer (shared by both groups) ─────
  // `orderedIndex` is the row's position within `ordered` (docs-first, then
  // no-docs). `dimmed` renders the de-emphasized (~50% opacity) variant used
  // by the "NO MIDAS DOCS YET" group; those rows stay fully selectable and
  // show a muted "live chat only" note in place of the readiness cluster.
  const renderMachineRow = (
    m: Machine,
    orderedIndex: number,
    dimmed: boolean
  ) => {
    const rowIndex = orderedIndex + platformOffset;
    const isFocused = rowIndex === focusedIndex;
    const brand = m.customBrandName || m.manufacturer?.name || "";
    const displayName = brand ? `${brand} ${m.model || ""}`.trim() : m.model || "—";
    const typeName = m.type?.name || "";
    const sn = m.serialNumber || "";
    return (
      <button
        key={m.id}
        ref={(el) => {
          machineBtnRefs.current[orderedIndex] = el;
        }}
        type="button"
        role="option"
        aria-selected={isFocused}
        onClick={() => pickMachine(m)}
        onMouseEnter={(e) => {
          setFocusedIndex(rowIndex);
          e.currentTarget.style.background = "var(--midas-v3-card-2)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = isFocused
            ? "var(--midas-v3-card-2)"
            : "transparent";
        }}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 10px",
          width: "100%",
          opacity: dimmed ? 0.5 : 1,
          background: isFocused ? "var(--midas-v3-card-2)" : "transparent",
          border: 0,
          borderRadius: 8,
          cursor: "pointer",
          textAlign: "left",
          fontFamily: "inherit",
          color: "var(--midas-v3-ink)",
          outline: "none",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              color: "var(--midas-v3-ink)",
              fontWeight: 500,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {displayName}
          </div>
          <div
            style={{
              fontSize: 11.5,
              color: "var(--midas-v3-muted)",
              display: "flex",
              gap: 10,
              marginTop: 1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {typeName && <span>{typeName}</span>}
            {sn && (
              <span className="mono" style={{ color: "var(--midas-v3-faint)" }}>
                SN {sn}
              </span>
            )}
            <span aria-hidden="true" style={{ color: "var(--midas-v3-faint)" }}>
              ·
            </span>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {formatLocation(m)}
            </span>
          </div>
        </div>
        {/* Readiness cluster (docs group) OR muted "live chat only" (no-docs
            group), rendered at the trailing edge of the row. Run-state / SR
            badges were removed per owner ruling 2026-08-06 — no live-state
            signal is shown until a full-telemetry pilot defines a real one. */}
        {dimmed ? (
          <span
            className="mono"
            style={{
              fontSize: 10,
              lineHeight: "12px",
              color: "var(--midas-v3-faint)",
              flexShrink: 0,
              whiteSpace: "nowrap",
            }}
          >
            {labelLiveChatOnly}
          </span>
        ) : (
          <NewChatReadinessCluster machine={m} />
        )}
      </button>
    );
  };

  if (!open) return null;

  // ── portal target — append to body so we sit above the v3 root and
  // any rail z-index stacks.
  const portalTarget = typeof document !== "undefined" ? document.body : null;
  if (!portalTarget) return null;

  // Wrap the modal in `.midas-v3-root.p-...` so token vars resolve. We
  // mirror the current theme onto the portal so the dialog matches the page.
  // `resolveV3PaletteClass()` replaced a 2-way inline sniff that fell through
  // to `p-machineid` for any palette other than claude — brand-blue dialog on
  // a teal/slate page (MIDAS-V3-PALETTES-TEAL-SLATE).
  const theme = resolveV3PaletteClass();

  const dialog = (
    <div
      className={`midas-v3-root ${theme}`}
      // Sit ABOVE the v3 layout. Pointer-events default to auto.
      //
      // R2 sub-task 4: `background: transparent` override is REQUIRED.
      // The `.midas-v3-root` class is applied here purely so the v3 token
      // custom properties resolve inside the portalled dialog (see the
      // comment block above at lines 499-504). But that class ALSO sets
      // `background: var(--midas-v3-bg)` (opaque cream) on its primary
      // selector in midas-v3-tokens.scss line 29-42. Without the explicit
      // transparent override here, the portal wrapper paints an opaque cream
      // fill over the entire viewport and the rgba backdrop below ends up
      // dimming the cream paint, not the actual page DOM behind it.
      // Net effect: dialog opens, page goes solid cream. The override
      // defeats that paint while keeping token resolution intact.
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1300,
        background: "transparent",
      }}
      role="presentation"
      onMouseDown={(e) => {
        // Backdrop close — only when the click starts on the backdrop itself,
        // not inside the dialog content.
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      {/* Backdrop */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(15, 23, 30, 0.32)",
          backdropFilter: "blur(2px)",
          WebkitBackdropFilter: "blur(2px)",
        }}
      />

      {/* Centered modal */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="midas-v3-new-chat-title"
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(560px, calc(100vw - 32px))",
          maxHeight: "min(640px, calc(100vh - 32px))",
          background: "var(--midas-v3-card)",
          border: "1px solid var(--midas-v3-border)",
          borderRadius: 14,
          boxShadow:
            "0 20px 60px rgba(15, 23, 30, 0.35), 0 4px 12px rgba(15, 23, 30, 0.12)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          color: "var(--midas-v3-ink)",
          fontFamily: "var(--font-sans)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "14px 18px",
            borderBottom: "1px solid var(--midas-v3-border-soft)",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <div
            id="midas-v3-new-chat-title"
            style={{
              fontWeight: 600,
              fontSize: 15,
              color: "var(--midas-v3-ink)",
            }}
          >
            {labelTitle}
          </div>
          <span className="midas-v3-eyebrow">{labelEyebrow}</span>
          <span style={{ flex: 1 }} />
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            aria-label={labelCloseAria}
            title={labelCloseAria}
            style={{
              width: 28,
              height: 28,
              padding: 0,
              borderRadius: 6,
              background: "transparent",
              border: "1px solid transparent",
              color: "var(--midas-v3-muted)",
              cursor: "pointer",
              display: "grid",
              placeItems: "center",
              fontFamily: "inherit",
              fontSize: 14,
              lineHeight: 1,
              transition: "background-color .12s, color .12s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--midas-v3-card-2)";
              e.currentTarget.style.color = "var(--midas-v3-ink)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = "var(--midas-v3-muted)";
            }}
          >
            ✕
          </button>
        </div>

        {/* Platform option (managers only) */}
        {isManager && (
          <button
            ref={platformBtnRef}
            type="button"
            onClick={pickPlatform}
            onMouseEnter={() => setFocusedIndex(0)}
            aria-label={`${labelPlatformTitle} — ${labelPlatformDesc}`}
            data-focused={focusedIndex === 0 ? "true" : undefined}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "14px 18px",
              margin: "12px 14px 0",
              background: "var(--midas-v3-accent-soft)",
              border:
                focusedIndex === 0
                  ? "1px solid var(--midas-v3-accent)"
                  : "1px solid var(--midas-v3-accent-line)",
              borderRadius: 10,
              cursor: "pointer",
              textAlign: "left",
              fontFamily: "inherit",
              color: "var(--midas-v3-ink)",
              outline: "none",
              transition: "border-color .12s, background-color .12s",
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 36,
                height: 36,
                borderRadius: 9,
                background: "var(--midas-v3-accent)",
                color: "#fff",
                display: "grid",
                placeItems: "center",
                fontFamily: "var(--font-mono)",
                fontWeight: 700,
                fontSize: 14,
                flexShrink: 0,
              }}
            >
              ◆
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                className="mono"
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--midas-v3-accent)",
                  letterSpacing: "0.08em",
                }}
              >
                {labelPlatformTitle}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "var(--midas-v3-ink-2)",
                  marginTop: 2,
                  whiteSpace: "normal",
                }}
              >
                {labelPlatformDesc}
              </div>
            </div>
            <span
              className="mono"
              style={{
                fontSize: 9,
                padding: "2px 7px",
                borderRadius: 999,
                background: "var(--midas-v3-accent)",
                color: "#fff",
                letterSpacing: "0.08em",
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {labelPlatformBadge}
            </span>
          </button>
        )}

        {/* Divider row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "16px 18px 6px",
          }}
        >
          <span className="midas-v3-eyebrow">{labelDivider}</span>
          <span
            aria-hidden="true"
            style={{
              flex: 1,
              height: 1,
              background: "var(--midas-v3-border-soft)",
            }}
          />
        </div>

        {/* Search input */}
        <div style={{ padding: "0 14px 10px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 12px",
              border: "1px solid var(--midas-v3-border)",
              background: "var(--midas-v3-bg-soft)",
              borderRadius: 8,
            }}
          >
            <span
              aria-hidden="true"
              style={{ color: "var(--midas-v3-faint)" }}
            >
              ⌕
            </span>
            <input
              ref={searchInputRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                // Reset cursor when the list re-shapes.
                setFocusedIndex(0);
              }}
              placeholder={labelSearchPlaceholder}
              aria-label={labelSearchPlaceholder}
              style={{
                flex: 1,
                border: 0,
                outline: 0,
                background: "transparent",
                fontFamily: "inherit",
                fontSize: 13,
                color: "var(--midas-v3-ink)",
                minWidth: 0,
              }}
            />
            <span
              className="mono"
              style={{
                fontSize: 10.5,
                color: "var(--midas-v3-faint)",
                flexShrink: 0,
              }}
            >
              {filtered.length}/{allMachines.length}
            </span>
          </div>
        </div>

        {/* Machine list */}
        <div
          role="listbox"
          aria-label={labelDivider}
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            padding: "0 8px 12px",
            minHeight: 80,
          }}
        >
          {allMachines.length === 0 ? (
            <div
              style={{
                padding: "24px 18px",
                textAlign: "center",
                fontSize: 13,
                color: "var(--midas-v3-muted)",
              }}
            >
              {labelNoMachines}
            </div>
          ) : filtered.length === 0 ? (
            <div
              style={{
                padding: "24px 18px",
                textAlign: "center",
                fontSize: 13,
                color: "var(--midas-v3-muted)",
              }}
            >
              {labelEmptyState}
            </div>
          ) : (
            <>
              {/* Docs group — machines with MIDAS docs, each with the
                  readiness cluster (doc chip + OEM star + (i) popover). */}
              {withDocs.map((m, i) => renderMachineRow(m, i, false))}

              {/* De-emphasized "NO MIDAS DOCS YET" group — dimmed but fully
                  selectable; ↑/↓ keyboard nav flows straight into it. */}
              {noDocs.length > 0 && (
                <div
                  role="group"
                  aria-label={labelNoDocsGroup}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    paddingTop: withDocs.length > 0 ? 8 : 0,
                  }}
                >
                  <div
                    role="presentation"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "0 12px 4px",
                    }}
                  >
                    <span
                      className="mono"
                      style={{
                        fontSize: 9.5,
                        lineHeight: "12px",
                        fontWeight: 600,
                        letterSpacing: "0.12em",
                        color: "var(--midas-v3-faint)",
                        flexShrink: 0,
                      }}
                    >
                      {labelNoDocsGroup}
                    </span>
                    <span
                      className="mono"
                      style={{
                        fontSize: 9.5,
                        lineHeight: "12px",
                        color: "var(--midas-v3-faint)",
                        background: "var(--midas-v3-bg-soft)",
                        borderRadius: 6,
                        padding: "1px 6px",
                        flexShrink: 0,
                      }}
                    >
                      {noDocs.length}
                    </span>
                    <span
                      aria-hidden="true"
                      style={{
                        flex: 1,
                        height: 1,
                        background: "var(--midas-v3-border-soft)",
                      }}
                    />
                  </div>
                  {noDocs.map((m, j) =>
                    renderMachineRow(m, withDocs.length + j, true)
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div
          className="mono"
          style={{
            padding: "10px 18px",
            borderTop: "1px solid var(--midas-v3-border-soft)",
            background: "var(--midas-v3-card-2)",
            fontSize: 10.5,
            color: "var(--midas-v3-faint)",
            letterSpacing: "0.04em",
            display: "flex",
            justifyContent: "space-between",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <span>
            {labelFooterNavigate} · {labelFooterSelect} · {labelFooterClose}
          </span>
          <span>{isManager ? labelManagerAccess : labelOperatorAccess}</span>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(dialog, portalTarget);
};

export default NewChatDialogV3;
